import * as z from 'zod';

export const SubmitJobRequest = z.object({
  source: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({
          localization: z.never().optional(),
          file: z.never().optional(),
          wixDataCollection: z
            .object({
              collectionId: z
                .string()
                .describe('ID of the data collection.')
                .min(1)
                .max(256)
                .optional(),
              filter: z
                .record(z.string(), z.any())
                .describe(
                  'Filter to apply to the items. Only items that pass the filter are moved.\n\nLearn more about building a query filter using [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
                )
                .optional()
                .nullable(),
              fields: z.array(z.string()).optional(),
            })
            .describe(
              'Wix data collection.\n\nYou can move items from native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
            ),
        }),
        z.object({
          wixDataCollection: z.never().optional(),
          file: z.never().optional(),
          localization: z
            .object({
              languages: z.array(z.string()).min(2).max(64).optional(),
            })
            .describe(
              'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
            ),
        }),
        z.object({
          wixDataCollection: z.never().optional(),
          localization: z.never().optional(),
          file: z
            .object({
              format: z.enum(['UNKNOWN', 'CSV', 'JSONL']).optional(),
              fileId: z
                .string()
                .describe(
                  "File ID as returned in the `fileId` property of the Create File Upload URL method's response."
                )
                .max(512)
                .optional(),
              url: z
                .string()
                .describe(
                  'URL where the source file is uploaded. Automatically generated by Wix.'
                )
                .max(2048)
                .optional(),
            })
            .describe(
              'Source file.\n\nYou can import data from files in CSV or JSONL formats. Uploading a file with an invalid format causes the movement job to fail.\n\nTo upload a file, call Create File Upload Url ([SDK](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/create-file-upload-url) | [REST](https://dev.wix.com/docs/rest/business-solutions/cms/data-movement-jobs/create-file-upload-url)) and use the `uploadUrl` from the response. For example:\n\n`curl --request PUT --upload-file "${path_to_file}" "${uploadUrl}"`'
            ),
        }),
      ])
    )
    .describe('Source to move data from.'),
  destination: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({
          localization: z.never().optional(),
          file: z.never().optional(),
          wixDataCollection: z
            .object({
              collectionId: z
                .string()
                .describe('ID of the destination collection.')
                .max(256)
                .optional(),
              writePolicy: z
                .enum(['OVERWRITE', 'SKIP_EXISTING', 'TRUNCATE_BEFORE'])
                .optional(),
            })
            .describe(
              'Wix data collection.\n\nYou can move items to native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
            ),
        }),
        z.object({
          wixDataCollection: z.never().optional(),
          file: z.never().optional(),
          localization: z
            .object({
              languages: z.array(z.string()).min(2).max(64).optional(),
            })
            .describe(
              'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
            ),
        }),
        z.object({
          wixDataCollection: z.never().optional(),
          localization: z.never().optional(),
          file: z
            .object({
              format: z.enum(['UNKNOWN', 'CSV', 'JSONL']).optional(),
              fileName: z
                .string()
                .describe('File name.')
                .max(512)
                .optional()
                .nullable(),
              url: z
                .string()
                .describe('File download URL.')
                .max(2048)
                .optional(),
            })
            .describe('Downloadable file with the moved items.'),
        }),
      ])
    )
    .describe('Destination to move data to.'),
  options: z
    .intersection(
      z.object({ name: z.string().describe('Job name.').optional() }),
      z.xor([
        z.object({
          sourceSiteId: z.never().optional(),
          destinationSiteId: z.never().optional(),
        }),
        z.object({
          destinationSiteId: z.never().optional(),
          sourceSiteId: z
            .string()
            .describe(
              'When moving data from the Wix data collection of a site other than the current one, the source site ID.\n\nBy default, the source site is the current site. However, you can move data from any site in the same [Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account).\n\n> **Note**: You can specify the ID of another source site or destination site, but not both.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          sourceSiteId: z.never().optional(),
          destinationSiteId: z
            .string()
            .describe(
              'When moving data to the Wix data collection of a site other than the current one, the destination site ID.\n\nBy default, the destination site is the current site. However, you can move data to any site in the same [Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account) by specifying its ID. You can retrieve this ID by calling [Get App Instance](https://dev.wix.com/docs/rest/app-management/app-instance/get-app-instance).\n\n> **Note**: You can specify the ID of another source site or destination site, but not both.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
      ])
    )
    .optional(),
});
export const SubmitJobResponse = z.object({
  job: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Job ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        startedAt: z
          .date()
          .describe('When the job was submitted.')
          .optional()
          .nullable(),
        status: z
          .enum([
            'UNINITIALIZED',
            'INITIALIZING',
            'IN_PROGRESS',
            'COMPLETED',
            'FAILED',
            'PARTIALLY_SUCCESSFUL',
          ])
          .describe('Current job status.')
          .optional(),
        source: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({
                wixDataCollection: z.never().optional(),
                localization: z.never().optional(),
                file: z.never().optional(),
              }),
              z.object({
                localization: z.never().optional(),
                file: z.never().optional(),
                wixDataCollection: z
                  .object({
                    collectionId: z
                      .string()
                      .describe('ID of the data collection.')
                      .min(1)
                      .max(256)
                      .optional(),
                    filter: z
                      .record(z.string(), z.any())
                      .describe(
                        'Filter to apply to the items. Only items that pass the filter are moved.\n\nLearn more about building a query filter using [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
                      )
                      .optional()
                      .nullable(),
                    fields: z.array(z.string()).optional(),
                  })
                  .describe(
                    'Wix data collection.\n\nYou can move items from native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                  ),
              }),
              z.object({
                wixDataCollection: z.never().optional(),
                file: z.never().optional(),
                localization: z
                  .object({
                    languages: z.array(z.string()).min(2).max(64).optional(),
                  })
                  .describe(
                    'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                  ),
              }),
              z.object({
                wixDataCollection: z.never().optional(),
                localization: z.never().optional(),
                file: z
                  .object({
                    format: z
                      .enum(['UNKNOWN', 'CSV', 'JSONL'])
                      .describe(
                        'File format. If not specified, automatically determined by Wix.'
                      )
                      .optional(),
                    fileId: z
                      .string()
                      .describe(
                        "File ID as returned in the `fileId` property of the Create File Upload URL method's response."
                      )
                      .max(512)
                      .optional(),
                    url: z
                      .string()
                      .describe(
                        'URL where the source file is uploaded. Automatically generated by Wix.'
                      )
                      .max(2048)
                      .optional(),
                  })
                  .describe(
                    'Source file.\n\nYou can import data from files in CSV or JSONL formats. Uploading a file with an invalid format causes the movement job to fail.\n\nTo upload a file, call Create File Upload Url ([SDK](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/create-file-upload-url) | [REST](https://dev.wix.com/docs/rest/business-solutions/cms/data-movement-jobs/create-file-upload-url)) and use the `uploadUrl` from the response. For example:\n\n`curl --request PUT --upload-file "${path_to_file}" "${uploadUrl}"`'
                  ),
              }),
            ])
          )
          .describe('Source to move data from.')
          .optional(),
        destination: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({
                wixDataCollection: z.never().optional(),
                localization: z.never().optional(),
                file: z.never().optional(),
              }),
              z.object({
                localization: z.never().optional(),
                file: z.never().optional(),
                wixDataCollection: z
                  .object({
                    collectionId: z
                      .string()
                      .describe('ID of the destination collection.')
                      .max(256)
                      .optional(),
                    writePolicy: z
                      .enum(['OVERWRITE', 'SKIP_EXISTING', 'TRUNCATE_BEFORE'])
                      .describe(
                        'When items already exist in the destination collection, the data writing policy.\n\nDefault: `OVERWRITE`.'
                      )
                      .optional(),
                  })
                  .describe(
                    'Wix data collection.\n\nYou can move items to native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                  ),
              }),
              z.object({
                wixDataCollection: z.never().optional(),
                file: z.never().optional(),
                localization: z
                  .object({
                    languages: z.array(z.string()).min(2).max(64).optional(),
                  })
                  .describe(
                    'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                  ),
              }),
              z.object({
                wixDataCollection: z.never().optional(),
                localization: z.never().optional(),
                file: z
                  .object({
                    format: z
                      .enum(['UNKNOWN', 'CSV', 'JSONL'])
                      .describe('File format.')
                      .optional(),
                    fileName: z
                      .string()
                      .describe('File name.')
                      .max(512)
                      .optional()
                      .nullable(),
                    url: z
                      .string()
                      .describe('File download URL.')
                      .max(2048)
                      .optional(),
                  })
                  .describe('Downloadable file with the moved items.'),
              }),
            ])
          )
          .describe('Destination to move data to.')
          .optional(),
        name: z.string().describe('Job name.').max(1024).optional(),
        progress: z
          .object({
            current: z
              .number()
              .int()
              .describe('Number of units already processed.')
              .optional(),
            total: z
              .number()
              .int()
              .describe('Total number of units to move.')
              .optional()
              .nullable(),
            itemsRejected: z
              .number()
              .int()
              .describe(
                'Number of items that failed to move to the destination.'
              )
              .optional(),
            itemsSuccessful: z
              .number()
              .int()
              .describe(
                'Number of items successfully moved to the destination.'
              )
              .optional(),
          })
          .describe(
            'When the job `status` is `IN_PROGRESS`, the progress details.'
          )
          .optional(),
        finishedAt: z
          .date()
          .describe(
            'Job finish time.\n\nA job is considered finished when its `status` updates to `COMPLETED`, `FAILED`, or `PARTIALLY_SUCCESSFUL`.'
          )
          .optional()
          .nullable(),
        error: z
          .object({
            code: z.string().describe('Error code.').optional(),
            description: z
              .string()
              .describe('Description of the error.')
              .optional(),
            data: z
              .record(z.string(), z.any())
              .describe('Data related to the error.')
              .optional()
              .nullable(),
          })
          .describe('Error information.')
          .optional(),
        sourceSiteId: z
          .string()
          .describe(
            'When moving data from a Wix data collection, the source site ID.\n\nBy default, the source site is the current site. To move data from [any site in the same Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account), specify the site ID, which you can retrieve by calling [Get App Instance](https://dev.wix.com/docs/rest/app-management/app-instance/get-app-instance).\n\n> **Note**: You can specify the ID of a source site or destination site, but not both.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        destinationSiteId: z
          .string()
          .describe(
            'When moving data to a Wix data collection, the destination site ID.\n\nBy default, the destination site is the current site. To move data to [any site in the same Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account), specify the site ID, which you can retrieve by calling [Get App Instance](https://dev.wix.com/docs/rest/app-management/app-instance/get-app-instance).\n\n> **Note**: You can specify the ID of a source site or destination site, but not both.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        logsRecorded: z
          .number()
          .int()
          .describe('Number of recorded logs.')
          .optional(),
        scheduleId: z
          .string()
          .describe(
            'Sync Schedule ID, if the job was triggered by a sync schedule.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Extended fields for custom metadata added by applications.'
          )
          .optional(),
        workflowId: z
          .string()
          .describe(
            'Workflow ID, if the job was created as a child step of a workflow.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({ partiallySuccessfulOptions: z.never().optional() }),
        z.object({
          partiallySuccessfulOptions: z
            .object({
              cause: z
                .string()
                .describe('Reason the job was partially successful.')
                .max(1000)
                .optional(),
            })
            .describe(
              "When the job's `status` is `PARTIALLY_SUCCESSFUL`, the additional details."
            ),
        }),
      ])
    )
    .describe('Details of the submitted job.')
    .optional(),
});
export const CreateFileUploadUrlRequest = z.object({
  options: z
    .object({
      fileName: z
        .string()
        .describe('Name of uploaded file.')
        .max(512)
        .optional()
        .nullable(),
    })
    .optional(),
});
export const CreateFileUploadUrlResponse = z.object({
  fileId: z.string().describe('File ID.').max(512).optional(),
  uploadUrl: z
    .string()
    .describe('URL to which you can upload the file.')
    .max(8192)
    .optional(),
});
export const GetJobRequest = z.object({
  jobId: z
    .string()
    .describe('Job ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetJobResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Job ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional(),
    startedAt: z
      .date()
      .describe('When the job was submitted.')
      .optional()
      .nullable(),
    status: z
      .enum([
        'UNINITIALIZED',
        'INITIALIZING',
        'IN_PROGRESS',
        'COMPLETED',
        'FAILED',
        'PARTIALLY_SUCCESSFUL',
      ])
      .describe('Current job status.')
      .optional(),
    source: z
      .intersection(
        z.object({}),
        z.xor([
          z.object({
            wixDataCollection: z.never().optional(),
            localization: z.never().optional(),
            file: z.never().optional(),
          }),
          z.object({
            localization: z.never().optional(),
            file: z.never().optional(),
            wixDataCollection: z
              .object({
                collectionId: z
                  .string()
                  .describe('ID of the data collection.')
                  .min(1)
                  .max(256)
                  .optional(),
                filter: z
                  .record(z.string(), z.any())
                  .describe(
                    'Filter to apply to the items. Only items that pass the filter are moved.\n\nLearn more about building a query filter using [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
                  )
                  .optional()
                  .nullable(),
                fields: z.array(z.string()).optional(),
              })
              .describe(
                'Wix data collection.\n\nYou can move items from native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
              ),
          }),
          z.object({
            wixDataCollection: z.never().optional(),
            file: z.never().optional(),
            localization: z
              .object({
                languages: z.array(z.string()).min(2).max(64).optional(),
              })
              .describe(
                'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
              ),
          }),
          z.object({
            wixDataCollection: z.never().optional(),
            localization: z.never().optional(),
            file: z
              .object({
                format: z
                  .enum(['UNKNOWN', 'CSV', 'JSONL'])
                  .describe(
                    'File format. If not specified, automatically determined by Wix.'
                  )
                  .optional(),
                fileId: z
                  .string()
                  .describe(
                    "File ID as returned in the `fileId` property of the Create File Upload URL method's response."
                  )
                  .max(512)
                  .optional(),
                url: z
                  .string()
                  .describe(
                    'URL where the source file is uploaded. Automatically generated by Wix.'
                  )
                  .max(2048)
                  .optional(),
              })
              .describe(
                'Source file.\n\nYou can import data from files in CSV or JSONL formats. Uploading a file with an invalid format causes the movement job to fail.\n\nTo upload a file, call Create File Upload Url ([SDK](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/create-file-upload-url) | [REST](https://dev.wix.com/docs/rest/business-solutions/cms/data-movement-jobs/create-file-upload-url)) and use the `uploadUrl` from the response. For example:\n\n`curl --request PUT --upload-file "${path_to_file}" "${uploadUrl}"`'
              ),
          }),
        ])
      )
      .describe('Source to move data from.')
      .optional(),
    destination: z
      .intersection(
        z.object({}),
        z.xor([
          z.object({
            wixDataCollection: z.never().optional(),
            localization: z.never().optional(),
            file: z.never().optional(),
          }),
          z.object({
            localization: z.never().optional(),
            file: z.never().optional(),
            wixDataCollection: z
              .object({
                collectionId: z
                  .string()
                  .describe('ID of the destination collection.')
                  .max(256)
                  .optional(),
                writePolicy: z
                  .enum(['OVERWRITE', 'SKIP_EXISTING', 'TRUNCATE_BEFORE'])
                  .describe(
                    'When items already exist in the destination collection, the data writing policy.\n\nDefault: `OVERWRITE`.'
                  )
                  .optional(),
              })
              .describe(
                'Wix data collection.\n\nYou can move items to native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
              ),
          }),
          z.object({
            wixDataCollection: z.never().optional(),
            file: z.never().optional(),
            localization: z
              .object({
                languages: z.array(z.string()).min(2).max(64).optional(),
              })
              .describe(
                'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
              ),
          }),
          z.object({
            wixDataCollection: z.never().optional(),
            localization: z.never().optional(),
            file: z
              .object({
                format: z
                  .enum(['UNKNOWN', 'CSV', 'JSONL'])
                  .describe('File format.')
                  .optional(),
                fileName: z
                  .string()
                  .describe('File name.')
                  .max(512)
                  .optional()
                  .nullable(),
                url: z
                  .string()
                  .describe('File download URL.')
                  .max(2048)
                  .optional(),
              })
              .describe('Downloadable file with the moved items.'),
          }),
        ])
      )
      .describe('Destination to move data to.')
      .optional(),
    name: z.string().describe('Job name.').max(1024).optional(),
    progress: z
      .object({
        current: z
          .number()
          .int()
          .describe('Number of units already processed.')
          .optional(),
        total: z
          .number()
          .int()
          .describe('Total number of units to move.')
          .optional()
          .nullable(),
        itemsRejected: z
          .number()
          .int()
          .describe('Number of items that failed to move to the destination.')
          .optional(),
        itemsSuccessful: z
          .number()
          .int()
          .describe('Number of items successfully moved to the destination.')
          .optional(),
      })
      .describe('When the job `status` is `IN_PROGRESS`, the progress details.')
      .optional(),
    finishedAt: z
      .date()
      .describe(
        'Job finish time.\n\nA job is considered finished when its `status` updates to `COMPLETED`, `FAILED`, or `PARTIALLY_SUCCESSFUL`.'
      )
      .optional()
      .nullable(),
    error: z
      .object({
        code: z.string().describe('Error code.').optional(),
        description: z
          .string()
          .describe('Description of the error.')
          .optional(),
        data: z
          .record(z.string(), z.any())
          .describe('Data related to the error.')
          .optional()
          .nullable(),
      })
      .describe('Error information.')
      .optional(),
    sourceSiteId: z
      .string()
      .describe(
        'When moving data from a Wix data collection, the source site ID.\n\nBy default, the source site is the current site. To move data from [any site in the same Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account), specify the site ID, which you can retrieve by calling [Get App Instance](https://dev.wix.com/docs/rest/app-management/app-instance/get-app-instance).\n\n> **Note**: You can specify the ID of a source site or destination site, but not both.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional(),
    destinationSiteId: z
      .string()
      .describe(
        'When moving data to a Wix data collection, the destination site ID.\n\nBy default, the destination site is the current site. To move data to [any site in the same Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account), specify the site ID, which you can retrieve by calling [Get App Instance](https://dev.wix.com/docs/rest/app-management/app-instance/get-app-instance).\n\n> **Note**: You can specify the ID of a source site or destination site, but not both.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional(),
    logsRecorded: z
      .number()
      .int()
      .describe('Number of recorded logs.')
      .optional(),
    scheduleId: z
      .string()
      .describe(
        'Sync Schedule ID, if the job was triggered by a sync schedule.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe('Extended fields for custom metadata added by applications.')
      .optional(),
    workflowId: z
      .string()
      .describe(
        'Workflow ID, if the job was created as a child step of a workflow.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
  }),
  z.xor([
    z.object({ partiallySuccessfulOptions: z.never().optional() }),
    z.object({
      partiallySuccessfulOptions: z
        .object({
          cause: z
            .string()
            .describe('Reason the job was partially successful.')
            .max(1000)
            .optional(),
        })
        .describe(
          "When the job's `status` is `PARTIALLY_SUCCESSFUL`, the additional details."
        ),
    }),
  ])
);
export const ListJobsRequest = z.object({
  options: z
    .object({
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Maximum number of items to return in the results.')
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Paging metadata.')
        .optional(),
    })
    .optional(),
});
export const ListJobsResponse = z.object({
  jobs: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Job ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          startedAt: z
            .date()
            .describe('When the job was submitted.')
            .optional()
            .nullable(),
          status: z
            .enum([
              'UNINITIALIZED',
              'INITIALIZING',
              'IN_PROGRESS',
              'COMPLETED',
              'FAILED',
              'PARTIALLY_SUCCESSFUL',
            ])
            .describe('Current job status.')
            .optional(),
          source: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({
                  wixDataCollection: z.never().optional(),
                  localization: z.never().optional(),
                  file: z.never().optional(),
                }),
                z.object({
                  localization: z.never().optional(),
                  file: z.never().optional(),
                  wixDataCollection: z
                    .object({
                      collectionId: z
                        .string()
                        .describe('ID of the data collection.')
                        .min(1)
                        .max(256)
                        .optional(),
                      filter: z
                        .record(z.string(), z.any())
                        .describe(
                          'Filter to apply to the items. Only items that pass the filter are moved.\n\nLearn more about building a query filter using [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
                        )
                        .optional()
                        .nullable(),
                      fields: z.array(z.string()).optional(),
                    })
                    .describe(
                      'Wix data collection.\n\nYou can move items from native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                    ),
                }),
                z.object({
                  wixDataCollection: z.never().optional(),
                  file: z.never().optional(),
                  localization: z
                    .object({
                      languages: z.array(z.string()).min(2).max(64).optional(),
                    })
                    .describe(
                      'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                    ),
                }),
                z.object({
                  wixDataCollection: z.never().optional(),
                  localization: z.never().optional(),
                  file: z
                    .object({
                      format: z
                        .enum(['UNKNOWN', 'CSV', 'JSONL'])
                        .describe(
                          'File format. If not specified, automatically determined by Wix.'
                        )
                        .optional(),
                      fileId: z
                        .string()
                        .describe(
                          "File ID as returned in the `fileId` property of the Create File Upload URL method's response."
                        )
                        .max(512)
                        .optional(),
                      url: z
                        .string()
                        .describe(
                          'URL where the source file is uploaded. Automatically generated by Wix.'
                        )
                        .max(2048)
                        .optional(),
                    })
                    .describe(
                      'Source file.\n\nYou can import data from files in CSV or JSONL formats. Uploading a file with an invalid format causes the movement job to fail.\n\nTo upload a file, call Create File Upload Url ([SDK](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/create-file-upload-url) | [REST](https://dev.wix.com/docs/rest/business-solutions/cms/data-movement-jobs/create-file-upload-url)) and use the `uploadUrl` from the response. For example:\n\n`curl --request PUT --upload-file "${path_to_file}" "${uploadUrl}"`'
                    ),
                }),
              ])
            )
            .describe('Source to move data from.')
            .optional(),
          destination: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({
                  wixDataCollection: z.never().optional(),
                  localization: z.never().optional(),
                  file: z.never().optional(),
                }),
                z.object({
                  localization: z.never().optional(),
                  file: z.never().optional(),
                  wixDataCollection: z
                    .object({
                      collectionId: z
                        .string()
                        .describe('ID of the destination collection.')
                        .max(256)
                        .optional(),
                      writePolicy: z
                        .enum(['OVERWRITE', 'SKIP_EXISTING', 'TRUNCATE_BEFORE'])
                        .describe(
                          'When items already exist in the destination collection, the data writing policy.\n\nDefault: `OVERWRITE`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Wix data collection.\n\nYou can move items to native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                    ),
                }),
                z.object({
                  wixDataCollection: z.never().optional(),
                  file: z.never().optional(),
                  localization: z
                    .object({
                      languages: z.array(z.string()).min(2).max(64).optional(),
                    })
                    .describe(
                      'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                    ),
                }),
                z.object({
                  wixDataCollection: z.never().optional(),
                  localization: z.never().optional(),
                  file: z
                    .object({
                      format: z
                        .enum(['UNKNOWN', 'CSV', 'JSONL'])
                        .describe('File format.')
                        .optional(),
                      fileName: z
                        .string()
                        .describe('File name.')
                        .max(512)
                        .optional()
                        .nullable(),
                      url: z
                        .string()
                        .describe('File download URL.')
                        .max(2048)
                        .optional(),
                    })
                    .describe('Downloadable file with the moved items.'),
                }),
              ])
            )
            .describe('Destination to move data to.')
            .optional(),
          name: z.string().describe('Job name.').max(1024).optional(),
          progress: z
            .object({
              current: z
                .number()
                .int()
                .describe('Number of units already processed.')
                .optional(),
              total: z
                .number()
                .int()
                .describe('Total number of units to move.')
                .optional()
                .nullable(),
              itemsRejected: z
                .number()
                .int()
                .describe(
                  'Number of items that failed to move to the destination.'
                )
                .optional(),
              itemsSuccessful: z
                .number()
                .int()
                .describe(
                  'Number of items successfully moved to the destination.'
                )
                .optional(),
            })
            .describe(
              'When the job `status` is `IN_PROGRESS`, the progress details.'
            )
            .optional(),
          finishedAt: z
            .date()
            .describe(
              'Job finish time.\n\nA job is considered finished when its `status` updates to `COMPLETED`, `FAILED`, or `PARTIALLY_SUCCESSFUL`.'
            )
            .optional()
            .nullable(),
          error: z
            .object({
              code: z.string().describe('Error code.').optional(),
              description: z
                .string()
                .describe('Description of the error.')
                .optional(),
              data: z
                .record(z.string(), z.any())
                .describe('Data related to the error.')
                .optional()
                .nullable(),
            })
            .describe('Error information.')
            .optional(),
          sourceSiteId: z
            .string()
            .describe(
              'When moving data from a Wix data collection, the source site ID.\n\nBy default, the source site is the current site. To move data from [any site in the same Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account), specify the site ID, which you can retrieve by calling [Get App Instance](https://dev.wix.com/docs/rest/app-management/app-instance/get-app-instance).\n\n> **Note**: You can specify the ID of a source site or destination site, but not both.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          destinationSiteId: z
            .string()
            .describe(
              'When moving data to a Wix data collection, the destination site ID.\n\nBy default, the destination site is the current site. To move data to [any site in the same Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account), specify the site ID, which you can retrieve by calling [Get App Instance](https://dev.wix.com/docs/rest/app-management/app-instance/get-app-instance).\n\n> **Note**: You can specify the ID of a source site or destination site, but not both.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          logsRecorded: z
            .number()
            .int()
            .describe('Number of recorded logs.')
            .optional(),
          scheduleId: z
            .string()
            .describe(
              'Sync Schedule ID, if the job was triggered by a sync schedule.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe(
              'Extended fields for custom metadata added by applications.'
            )
            .optional(),
          workflowId: z
            .string()
            .describe(
              'Workflow ID, if the job was created as a child step of a workflow.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        }),
        z.xor([
          z.object({ partiallySuccessfulOptions: z.never().optional() }),
          z.object({
            partiallySuccessfulOptions: z
              .object({
                cause: z
                  .string()
                  .describe('Reason the job was partially successful.')
                  .max(1000)
                  .optional(),
              })
              .describe(
                "When the job's `status` is `PARTIALLY_SUCCESSFUL`, the additional details."
              ),
          }),
        ])
      )
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const QueryJobsRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor paging options.\n\nLearn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).'
            ),
        }),
      ])
    )
    .describe(
      'Query preferences. Learn more about [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language).'
    ),
});
export const QueryJobsResponse = z.object({
  jobs: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Job ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          startedAt: z
            .date()
            .describe('When the job was submitted.')
            .optional()
            .nullable(),
          status: z
            .enum([
              'UNINITIALIZED',
              'INITIALIZING',
              'IN_PROGRESS',
              'COMPLETED',
              'FAILED',
              'PARTIALLY_SUCCESSFUL',
            ])
            .describe('Current job status.')
            .optional(),
          source: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({
                  wixDataCollection: z.never().optional(),
                  localization: z.never().optional(),
                  file: z.never().optional(),
                }),
                z.object({
                  localization: z.never().optional(),
                  file: z.never().optional(),
                  wixDataCollection: z
                    .object({
                      collectionId: z
                        .string()
                        .describe('ID of the data collection.')
                        .min(1)
                        .max(256)
                        .optional(),
                      filter: z
                        .record(z.string(), z.any())
                        .describe(
                          'Filter to apply to the items. Only items that pass the filter are moved.\n\nLearn more about building a query filter using [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
                        )
                        .optional()
                        .nullable(),
                      fields: z.array(z.string()).optional(),
                    })
                    .describe(
                      'Wix data collection.\n\nYou can move items from native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                    ),
                }),
                z.object({
                  wixDataCollection: z.never().optional(),
                  file: z.never().optional(),
                  localization: z
                    .object({
                      languages: z.array(z.string()).min(2).max(64).optional(),
                    })
                    .describe(
                      'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                    ),
                }),
                z.object({
                  wixDataCollection: z.never().optional(),
                  localization: z.never().optional(),
                  file: z
                    .object({
                      format: z
                        .enum(['UNKNOWN', 'CSV', 'JSONL'])
                        .describe(
                          'File format. If not specified, automatically determined by Wix.'
                        )
                        .optional(),
                      fileId: z
                        .string()
                        .describe(
                          "File ID as returned in the `fileId` property of the Create File Upload URL method's response."
                        )
                        .max(512)
                        .optional(),
                      url: z
                        .string()
                        .describe(
                          'URL where the source file is uploaded. Automatically generated by Wix.'
                        )
                        .max(2048)
                        .optional(),
                    })
                    .describe(
                      'Source file.\n\nYou can import data from files in CSV or JSONL formats. Uploading a file with an invalid format causes the movement job to fail.\n\nTo upload a file, call Create File Upload Url ([SDK](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/create-file-upload-url) | [REST](https://dev.wix.com/docs/rest/business-solutions/cms/data-movement-jobs/create-file-upload-url)) and use the `uploadUrl` from the response. For example:\n\n`curl --request PUT --upload-file "${path_to_file}" "${uploadUrl}"`'
                    ),
                }),
              ])
            )
            .describe('Source to move data from.')
            .optional(),
          destination: z
            .intersection(
              z.object({}),
              z.xor([
                z.object({
                  wixDataCollection: z.never().optional(),
                  localization: z.never().optional(),
                  file: z.never().optional(),
                }),
                z.object({
                  localization: z.never().optional(),
                  file: z.never().optional(),
                  wixDataCollection: z
                    .object({
                      collectionId: z
                        .string()
                        .describe('ID of the destination collection.')
                        .max(256)
                        .optional(),
                      writePolicy: z
                        .enum(['OVERWRITE', 'SKIP_EXISTING', 'TRUNCATE_BEFORE'])
                        .describe(
                          'When items already exist in the destination collection, the data writing policy.\n\nDefault: `OVERWRITE`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Wix data collection.\n\nYou can move items to native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                    ),
                }),
                z.object({
                  wixDataCollection: z.never().optional(),
                  file: z.never().optional(),
                  localization: z
                    .object({
                      languages: z.array(z.string()).min(2).max(64).optional(),
                    })
                    .describe(
                      'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                    ),
                }),
                z.object({
                  wixDataCollection: z.never().optional(),
                  localization: z.never().optional(),
                  file: z
                    .object({
                      format: z
                        .enum(['UNKNOWN', 'CSV', 'JSONL'])
                        .describe('File format.')
                        .optional(),
                      fileName: z
                        .string()
                        .describe('File name.')
                        .max(512)
                        .optional()
                        .nullable(),
                      url: z
                        .string()
                        .describe('File download URL.')
                        .max(2048)
                        .optional(),
                    })
                    .describe('Downloadable file with the moved items.'),
                }),
              ])
            )
            .describe('Destination to move data to.')
            .optional(),
          name: z.string().describe('Job name.').max(1024).optional(),
          progress: z
            .object({
              current: z
                .number()
                .int()
                .describe('Number of units already processed.')
                .optional(),
              total: z
                .number()
                .int()
                .describe('Total number of units to move.')
                .optional()
                .nullable(),
              itemsRejected: z
                .number()
                .int()
                .describe(
                  'Number of items that failed to move to the destination.'
                )
                .optional(),
              itemsSuccessful: z
                .number()
                .int()
                .describe(
                  'Number of items successfully moved to the destination.'
                )
                .optional(),
            })
            .describe(
              'When the job `status` is `IN_PROGRESS`, the progress details.'
            )
            .optional(),
          finishedAt: z
            .date()
            .describe(
              'Job finish time.\n\nA job is considered finished when its `status` updates to `COMPLETED`, `FAILED`, or `PARTIALLY_SUCCESSFUL`.'
            )
            .optional()
            .nullable(),
          error: z
            .object({
              code: z.string().describe('Error code.').optional(),
              description: z
                .string()
                .describe('Description of the error.')
                .optional(),
              data: z
                .record(z.string(), z.any())
                .describe('Data related to the error.')
                .optional()
                .nullable(),
            })
            .describe('Error information.')
            .optional(),
          sourceSiteId: z
            .string()
            .describe(
              'When moving data from a Wix data collection, the source site ID.\n\nBy default, the source site is the current site. To move data from [any site in the same Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account), specify the site ID, which you can retrieve by calling [Get App Instance](https://dev.wix.com/docs/rest/app-management/app-instance/get-app-instance).\n\n> **Note**: You can specify the ID of a source site or destination site, but not both.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          destinationSiteId: z
            .string()
            .describe(
              'When moving data to a Wix data collection, the destination site ID.\n\nBy default, the destination site is the current site. To move data to [any site in the same Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account), specify the site ID, which you can retrieve by calling [Get App Instance](https://dev.wix.com/docs/rest/app-management/app-instance/get-app-instance).\n\n> **Note**: You can specify the ID of a source site or destination site, but not both.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          logsRecorded: z
            .number()
            .int()
            .describe('Number of recorded logs.')
            .optional(),
          scheduleId: z
            .string()
            .describe(
              'Sync Schedule ID, if the job was triggered by a sync schedule.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe(
              'Extended fields for custom metadata added by applications.'
            )
            .optional(),
          workflowId: z
            .string()
            .describe(
              'Workflow ID, if the job was created as a child step of a workflow.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        }),
        z.xor([
          z.object({ partiallySuccessfulOptions: z.never().optional() }),
          z.object({
            partiallySuccessfulOptions: z
              .object({
                cause: z
                  .string()
                  .describe('Reason the job was partially successful.')
                  .max(1000)
                  .optional(),
              })
              .describe(
                "When the job's `status` is `PARTIALLY_SUCCESSFUL`, the additional details."
              ),
          }),
        ])
      )
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const TerminateJobRequest = z.object({
  jobId: z
    .string()
    .describe('ID of the job to terminate.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const TerminateJobResponse = z.object({
  job: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Job ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        startedAt: z
          .date()
          .describe('When the job was submitted.')
          .optional()
          .nullable(),
        status: z
          .enum([
            'UNINITIALIZED',
            'INITIALIZING',
            'IN_PROGRESS',
            'COMPLETED',
            'FAILED',
            'PARTIALLY_SUCCESSFUL',
          ])
          .describe('Current job status.')
          .optional(),
        source: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({
                wixDataCollection: z.never().optional(),
                localization: z.never().optional(),
                file: z.never().optional(),
              }),
              z.object({
                localization: z.never().optional(),
                file: z.never().optional(),
                wixDataCollection: z
                  .object({
                    collectionId: z
                      .string()
                      .describe('ID of the data collection.')
                      .min(1)
                      .max(256)
                      .optional(),
                    filter: z
                      .record(z.string(), z.any())
                      .describe(
                        'Filter to apply to the items. Only items that pass the filter are moved.\n\nLearn more about building a query filter using [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
                      )
                      .optional()
                      .nullable(),
                    fields: z.array(z.string()).optional(),
                  })
                  .describe(
                    'Wix data collection.\n\nYou can move items from native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                  ),
              }),
              z.object({
                wixDataCollection: z.never().optional(),
                file: z.never().optional(),
                localization: z
                  .object({
                    languages: z.array(z.string()).min(2).max(64).optional(),
                  })
                  .describe(
                    'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                  ),
              }),
              z.object({
                wixDataCollection: z.never().optional(),
                localization: z.never().optional(),
                file: z
                  .object({
                    format: z
                      .enum(['UNKNOWN', 'CSV', 'JSONL'])
                      .describe(
                        'File format. If not specified, automatically determined by Wix.'
                      )
                      .optional(),
                    fileId: z
                      .string()
                      .describe(
                        "File ID as returned in the `fileId` property of the Create File Upload URL method's response."
                      )
                      .max(512)
                      .optional(),
                    url: z
                      .string()
                      .describe(
                        'URL where the source file is uploaded. Automatically generated by Wix.'
                      )
                      .max(2048)
                      .optional(),
                  })
                  .describe(
                    'Source file.\n\nYou can import data from files in CSV or JSONL formats. Uploading a file with an invalid format causes the movement job to fail.\n\nTo upload a file, call Create File Upload Url ([SDK](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/create-file-upload-url) | [REST](https://dev.wix.com/docs/rest/business-solutions/cms/data-movement-jobs/create-file-upload-url)) and use the `uploadUrl` from the response. For example:\n\n`curl --request PUT --upload-file "${path_to_file}" "${uploadUrl}"`'
                  ),
              }),
            ])
          )
          .describe('Source to move data from.')
          .optional(),
        destination: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({
                wixDataCollection: z.never().optional(),
                localization: z.never().optional(),
                file: z.never().optional(),
              }),
              z.object({
                localization: z.never().optional(),
                file: z.never().optional(),
                wixDataCollection: z
                  .object({
                    collectionId: z
                      .string()
                      .describe('ID of the destination collection.')
                      .max(256)
                      .optional(),
                    writePolicy: z
                      .enum(['OVERWRITE', 'SKIP_EXISTING', 'TRUNCATE_BEFORE'])
                      .describe(
                        'When items already exist in the destination collection, the data writing policy.\n\nDefault: `OVERWRITE`.'
                      )
                      .optional(),
                  })
                  .describe(
                    'Wix data collection.\n\nYou can move items to native Wix collections, [Wix app collections](https://dev.wix.com/docs/develop-websites/articles/databases/wix-data/collections/working-with-wix-app-collections-and-code), and [external database collections](https://dev.wix.com/docs/develop-websites/articles/databases/external-databases/overview/integrating-external-databases-with-your-wix-site).'
                  ),
              }),
              z.object({
                wixDataCollection: z.never().optional(),
                file: z.never().optional(),
                localization: z
                  .object({
                    languages: z.array(z.string()).min(2).max(64).optional(),
                  })
                  .describe(
                    'Localized CMS content. Available when the site has [Wix Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) installed.\n\nLearn more about working with [translated CMS content](https://support.wix.com/en/article/wix-multilingual-translating-cms-collection-content).'
                  ),
              }),
              z.object({
                wixDataCollection: z.never().optional(),
                localization: z.never().optional(),
                file: z
                  .object({
                    format: z
                      .enum(['UNKNOWN', 'CSV', 'JSONL'])
                      .describe('File format.')
                      .optional(),
                    fileName: z
                      .string()
                      .describe('File name.')
                      .max(512)
                      .optional()
                      .nullable(),
                    url: z
                      .string()
                      .describe('File download URL.')
                      .max(2048)
                      .optional(),
                  })
                  .describe('Downloadable file with the moved items.'),
              }),
            ])
          )
          .describe('Destination to move data to.')
          .optional(),
        name: z.string().describe('Job name.').max(1024).optional(),
        progress: z
          .object({
            current: z
              .number()
              .int()
              .describe('Number of units already processed.')
              .optional(),
            total: z
              .number()
              .int()
              .describe('Total number of units to move.')
              .optional()
              .nullable(),
            itemsRejected: z
              .number()
              .int()
              .describe(
                'Number of items that failed to move to the destination.'
              )
              .optional(),
            itemsSuccessful: z
              .number()
              .int()
              .describe(
                'Number of items successfully moved to the destination.'
              )
              .optional(),
          })
          .describe(
            'When the job `status` is `IN_PROGRESS`, the progress details.'
          )
          .optional(),
        finishedAt: z
          .date()
          .describe(
            'Job finish time.\n\nA job is considered finished when its `status` updates to `COMPLETED`, `FAILED`, or `PARTIALLY_SUCCESSFUL`.'
          )
          .optional()
          .nullable(),
        error: z
          .object({
            code: z.string().describe('Error code.').optional(),
            description: z
              .string()
              .describe('Description of the error.')
              .optional(),
            data: z
              .record(z.string(), z.any())
              .describe('Data related to the error.')
              .optional()
              .nullable(),
          })
          .describe('Error information.')
          .optional(),
        sourceSiteId: z
          .string()
          .describe(
            'When moving data from a Wix data collection, the source site ID.\n\nBy default, the source site is the current site. To move data from [any site in the same Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account), specify the site ID, which you can retrieve by calling [Get App Instance](https://dev.wix.com/docs/rest/app-management/app-instance/get-app-instance).\n\n> **Note**: You can specify the ID of a source site or destination site, but not both.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        destinationSiteId: z
          .string()
          .describe(
            'When moving data to a Wix data collection, the destination site ID.\n\nBy default, the destination site is the current site. To move data to [any site in the same Wix Account](https://support.wix.com/en/article/managing-multiple-sites-under-one-account), specify the site ID, which you can retrieve by calling [Get App Instance](https://dev.wix.com/docs/rest/app-management/app-instance/get-app-instance).\n\n> **Note**: You can specify the ID of a source site or destination site, but not both.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        logsRecorded: z
          .number()
          .int()
          .describe('Number of recorded logs.')
          .optional(),
        scheduleId: z
          .string()
          .describe(
            'Sync Schedule ID, if the job was triggered by a sync schedule.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Extended fields for custom metadata added by applications.'
          )
          .optional(),
        workflowId: z
          .string()
          .describe(
            'Workflow ID, if the job was created as a child step of a workflow.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({ partiallySuccessfulOptions: z.never().optional() }),
        z.object({
          partiallySuccessfulOptions: z
            .object({
              cause: z
                .string()
                .describe('Reason the job was partially successful.')
                .max(1000)
                .optional(),
            })
            .describe(
              "When the job's `status` is `PARTIALLY_SUCCESSFUL`, the additional details."
            ),
        }),
      ])
    )
    .describe('Details of the terminated job.')
    .optional(),
});
export const ListMovementLogsRequest = z.object({
  jobId: z
    .string()
    .describe('ID of the job for which to list logs.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Maximum number of items to return in the results.')
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Paging metadata.')
        .optional(),
    })
    .optional(),
});
export const ListMovementLogsResponse = z.object({
  logs: z
    .array(
      z.object({
        sourceItemId: z
          .string()
          .describe(
            'ID of the item in the source, such as a Wix Data Item or a row number in a CSV file.'
          )
          .max(512)
          .optional(),
        failure: z
          .object({
            code: z.string().describe('Error code.').optional(),
            description: z
              .string()
              .describe('Description of the error.')
              .optional(),
            data: z
              .record(z.string(), z.any())
              .describe('Data related to the error.')
              .optional()
              .nullable(),
          })
          .describe('Details about the failure if moving the item fails.')
          .optional(),
        details: z
          .record(z.string(), z.any())
          .describe(
            'Additional details provided when moving the item succeeds with details.\n\n> **Note**: Each destination type determines the structure of its `details` object.'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
